import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import DirectoryPage from "@/pages/DirectoryPage";
import { AuthProvider } from "@/context/AuthContext";
import { SiteSchema } from "@/components/Seo";
import { Toaster } from "@/components/ui/sonner";
import { NarrationProvider } from "@/context/NarrationContext";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import "@/App.css";

const ListingDetailPage = lazy(() => import("@/pages/ListingDetailPage"));
const AdvertisePage = lazy(() => import("@/pages/AdvertisePage"));
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const ClinicalGuidePage = lazy(() => import("@/pages/ClinicalGuidePage"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const FlyerPage = lazy(() => import("@/pages/FlyerPage"));
const PrintPage = lazy(() => import("@/pages/PrintPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AiChatWidget = lazy(() => import("@/components/AiChatWidget"));
const AssistantsPage = lazy(() => import("@/pages/AssistantsPage"));
const ProgramDetailPage = lazy(() => import("@/pages/ProgramDetailPage"));
const CountyPage = lazy(() => import("@/pages/CountyPage"));
const TravelPage = lazy(() => import("@/pages/TravelPage"));
const EmbedPage = lazy(() => import("@/pages/EmbedPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const PartnerProfilePage = lazy(() => import("@/pages/PartnerProfilePage"));
const PartnerRequestsPage = lazy(() => import("@/pages/PartnerRequestsPage"));
const ChallengesPage = lazy(() => import("@/pages/ChallengesPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AreasPage = lazy(() => import("@/pages/AreasPage"));
const AreaDetailPage = lazy(() => import("@/pages/AreaDetailPage"));
const StatePage = lazy(() => import("@/pages/StatePage"));
const OwnerPortalPage = lazy(() => import("@/pages/OwnerPortalPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const BadgePage = lazy(() => import("@/pages/BadgePage"));
const ChallengeSharePage = lazy(() => import("@/pages/ChallengeSharePage"));
const EventSharePage = lazy(() => import("@/pages/EventSharePage"));
const MemberProfilePage = lazy(() => import("@/pages/MemberProfilePage"));
const WellnessHubPage = lazy(() => import("@/pages/WellnessHubPage"));
const FoundersCornerPage = lazy(() => import("@/pages/FoundersCornerPage"));
const CommunityPartnersPage = lazy(() => import("@/pages/CommunityPartnersPage"));
const HubContentPage = lazy(() => import("@/pages/HubContentPage"));
const HubLibraryPage = lazy(() => import("@/pages/HubLibraryPage"));
const SponsorReportPage = lazy(() => import("@/pages/SponsorReportPage"));
const PrivateMessagesPage = lazy(() => import("@/pages/PrivateMessagesPage"));
const SupplementAnalysisPage = lazy(() => import("@/pages/SupplementAnalysisPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

function RouteFallback() {
  return <div className="min-h-[45vh] flex items-center justify-center bg-slate-50" data-testid="route-loading"><p className="font-body text-sm text-gray-500">Loading…</p></div>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
        <NarrationProvider>
        <SiteSchema />
        <TooltipProvider delayDuration={300}>
          <div id="main-content">
          <RouteErrorBoundary><Suspense fallback={<RouteFallback />}><Routes>
            <Route path="/" element={<DirectoryPage />} />
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/clinical-guide" element={<ClinicalGuidePage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/assistants" element={<AssistantsPage />} />
            <Route path="/programs/:slug" element={<ProgramDetailPage />} />
            <Route path="/founders-corner" element={<FoundersCornerPage />} />
            <Route path="/community-partners" element={<CommunityPartnersPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/flyer/:id" element={<FlyerPage />} />
            <Route path="/print/:id" element={<PrintPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/county/:county" element={<CountyPage />} />
            <Route path="/travel" element={<TravelPage />} />
            <Route path="/embed" element={<EmbedPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/partners/profile" element={<PartnerProfilePage />} />
            <Route path="/partners/requests" element={<PartnerRequestsPage />} />
            <Route path="/challenges" element={<ChallengesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/for-business" element={<PricingPage />} />
            <Route path="/areas" element={<AreasPage />} />
            <Route path="/areas/:slug" element={<AreaDetailPage />} />
            <Route path="/state/:slug" element={<StatePage />} />
            <Route path="/owner" element={<OwnerPortalPage />} />
            <Route path="/messages" element={<PrivateMessagesPage />} />
            <Route path="/supplement-analysis" element={<SupplementAnalysisPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/badges/:id" element={<BadgePage />} />
            <Route path="/challenges/:id" element={<ChallengeSharePage />} />
            <Route path="/events/:id" element={<EventSharePage />} />
            <Route path="/members/:id" element={<MemberProfilePage />} />
            <Route path="/wellness-hub" element={<WellnessHubPage />} />
            <Route path="/wellness-hub/library" element={<HubLibraryPage />} />
            <Route path="/wellness-hub/:slug" element={<HubContentPage />} />
            <Route path="/sponsor-report/:token" element={<SponsorReportPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes></Suspense></RouteErrorBoundary>
          </div>
          <Suspense fallback={null}><AiChatWidget /></Suspense>
          <Toaster position="top-center" richColors />
        </TooltipProvider>
        </NarrationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
